import { create } from "zustand";
import { ProfileState } from "../types/api"; // імпортуємо нові типи
import { getProfile, getUserProfileLanguage } from "../api/profileApi";
import { devtools } from "zustand/middleware";
import { uploadCV } from "../api/userApi";
import { editTooltipDisplay, tourDisplay } from "../api/profileEditingApi";

const loadFromLocalStorage = <T>(key: string, defaultValue: T) => {
  const saved = localStorage.getItem(key);
  return saved ? JSON.parse(saved) : defaultValue;
};

const useProfileStore = create<ProfileState>()(
  devtools(
    (set, get) => ({
      profile: null,
      loading: false,
      error: null,
      changes: loadFromLocalStorage("changes", null),
      userMarketChanges: loadFromLocalStorage("userMarketChanges", null),
      profileLanguage: loadFromLocalStorage("profileLanguage", null),

      fetchProfile: async () => {
        set({ loading: true, error: null });
        const cvProcessing = localStorage.getItem("cvProcessing");
        try {
          const language = get().profileLanguage;

          const response = await getProfile(
            Object.keys(language!)[0] ? Object.keys(language!)[0] : ""
          );

          if (response.status !== "success") {
            throw new Error("Failed to fetch profile details");
          }

          if (JSON.parse(cvProcessing!) === "processing") {
            set({
              profile: {
                ...response.data,
                skills: [],
                experiences: [],
                educations: [],
                certifications: [],
                hobbies: [],
                languages: [],
                // profile_completeness: 0,
                contact_details: null,
              },
              loading: false,
            });
          } else {
            set({ profile: response.data, loading: false });
          }
        } catch (error: any) {
          set({ error: error.message, loading: false });
        }
      },
      getProfileLanguage: async () => {
        set({ loading: true, error: null });
        try {
          const response = await getUserProfileLanguage();

          if (response.status !== "success") {
            throw new Error("Failed to fetch profile details");
          }

          localStorage.setItem(
            "profileLanguage",
            JSON.stringify(response.data)
          );

          set({ profileLanguage: response.data, loading: false, error: null });
        } catch (error: any) {
          set({ error: error.message, loading: false });
        }
      },
      uploadProfileCv: async ({ cvFile }: { cvFile: File }) => {
        try {
          const response = await uploadCV({ cvFile });

          if (response.status !== "success") {
            throw new Error("Failed to fetch cv details");
          }

          set((state) => ({
            profile: state.profile && {
              ...state.profile,
              user_cv: response.data,
            },
          }));
          return response;
        } catch (error: any) {
          set({ error: error.message, loading: false });
          throw error;
        }
      },

      editTooltipShow: async (credential: boolean) => {
        try {
          await editTooltipDisplay(credential);

          set((state) => ({
            profile: state.profile && {
              ...state.profile,
              display_preferences: {
                ...state.profile.display_preferences,
                hide_profile_completeness_tooltip: credential,
              },
            },
          }));
          return;
        } catch (error) {
          console.log(error);
        }
      },

      tourButtonShow: async (credential: { [key: string]: boolean }) => {
        try {
          await tourDisplay(credential);

          set((state) => ({
            profile: state.profile && {
              ...state.profile,
              display_preferences: {
                ...state.profile.display_preferences,
                ...credential,
              },
            },
          }));
          return;
        } catch (error) {
          console.log(error);
        }
      },
      resetProfileState: () => {
        set({
          profile: null,
        });
      },

      setChanges: (t) => {
        localStorage.setItem("changes", JSON.stringify(t));
        set({
          changes: t,
        });
      },

      setUserMarketChanges: (t) => {
        localStorage.setItem("userMarketChanges", JSON.stringify(t));
        set({
          userMarketChanges: t,
        });
      },

      resetProfileFields: () => {
        set((state) => ({
          profile: state.profile
            ? {
                ...state.profile,
                skills: [],
                experiences: [],
                educations: [],
                certifications: [],
                hobbies: [],
                languages: [],
                contact_details: null,
              }
            : null,
        }));
      },
    }),
    { name: "profileStore" }
  )
);

export default useProfileStore;
